import {
  ErrorMessage,
  Field,
  type FieldInputProps,
  type FieldMetaProps,
  type FieldProps,
} from 'formik';

import {
  Box,
  FormControl,
  FormErrorMessage,
  HStack,
  Text,
  Tooltip,
  WrenchIcon,
  Badge,
  CircleCheckmarkIcon,
  CircleXIcon,
  DollarSignIcon,
  IconButton,
  ShareExternalIcon,
} from '@frontend/components';

import {
  FeatureFlagData,
  FeatureFlagTierSetting,
  FeatureFlagType,
  FeatureFlagAvailability,
} from 'modules/common/drawers/featureFlagDrawer/types';

import {
  CheckboxInput,
  NumberInput,
  StringSelectInput,
  TierCell,
} from './components';

const getAvailabilityBadgeProps = (availability: FeatureFlagAvailability) => {
  switch (availability) {
    case FeatureFlagAvailability.CheckEntitlement:
      return {
        colorScheme: 'success' as const,
        children: 'Entitlement',
      };
    case FeatureFlagAvailability.ConsultRD:
      return {
        colorScheme: 'warning' as const,
        children: 'Consult R&D',
      };
    case FeatureFlagAvailability.CheckInstructions:
      return {
        colorScheme: 'info' as const,
        children: 'Check Instructions',
      };
    case FeatureFlagAvailability.NotAvailable:
      return {
        colorScheme: 'error' as const,
        children: 'Not Available',
      };
    default:
      return {
        colorScheme: 'gray' as const,
        children: availability,
      };
  }
};

export const getBooleanTierStatus = (
  setting: FeatureFlagTierSetting,
  contracted: boolean,
): string => {
  if (setting === FeatureFlagTierSetting.Upsell) {
    return 'Upsell';
  }

  if (setting === FeatureFlagTierSetting.Enabled) {
    return contracted ? 'Contracted only' : 'Enabled';
  }

  return 'Disabled';
};

export const getBooleanTierIcon = (
  setting: FeatureFlagTierSetting,
  contracted: boolean,
) => {
  if (setting === FeatureFlagTierSetting.Upsell) {
    return (
      <Box
        display="inline-flex"
        alignItems="center"
        justifyContent="center"
        boxSize={3}
        flexShrink={0}
      >
        <Box
          width="18px"
          height="18px"
          borderRadius="full"
          bg="alert.warning"
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
        >
          <DollarSignIcon boxSize={2} color="feature.onInteraction" />
        </Box>
      </Box>
    );
  }

  if (setting === FeatureFlagTierSetting.Enabled) {
    if (contracted) {
      return (
        <Box
          display="inline-flex"
          alignItems="center"
          justifyContent="center"
          boxSize={3}
          flexShrink={0}
          border="2px solid"
          borderColor="alert.warning"
          borderRadius="1000px"
          boxSizing="border-box"
        >
          <Box
            display="inline-flex"
            alignItems="center"
            justifyContent="center"
            transform="scale(0.833)"
            transformOrigin="center"
          >
            <CircleCheckmarkIcon boxSize={3} color="alert.success" />
          </Box>
        </Box>
      );
    }

    return <CircleCheckmarkIcon boxSize={3} color="alert.success" />;
  }

  return <CircleXIcon boxSize={3} color="alert.error" />;
};

export const FormFieldRow = ({
  flagKey,
  flagData,
  index,
}: {
  flagKey: string;
  flagData: FeatureFlagData;
  index: number;
}) => {
  const {
    explanation,
    deprecate,
    type,
    displayName,
    availability,
    tierInfo,
    contracted,
    learnMoreLink,
  } = flagData;

  return (
    <Field name={flagKey}>
      {({ field, form, meta }: FieldProps<string | number | boolean>) => {
        const { name: fieldName } = field;
        const { errors = {}, touched = {} } = form;
        const isInvalid = fieldName
          ? touched[fieldName] && Boolean(errors[fieldName])
          : false;
        const isRequired =
          type === FeatureFlagType.Number || type === FeatureFlagType.String;

        const valueChanged = meta.initialValue !== meta.value;
        return (
          <Box
            as="tr"
            bg={
              valueChanged
                ? 'interaction.modified'
                : index % 2 === 1
                ? 'page.plateLight'
                : 'transparent'
            }
            _hover={
              !valueChanged
                ? {
                    bg: 'interaction.surfaceHover',
                  }
                : undefined
            }
            sx={{
              h: 4.5,

              'td:first-of-type': {
                borderTopLeftRadius: 'md',
                borderBottomLeftRadius: 'md',
              },
              'td:last-of-type': {
                borderTopRightRadius: 'md',
                borderBottomRightRadius: 'md',
              },
            }}
          >
            {/* Leftmost cell: title */}
            <Box as="td" pl={1} pr={0.5} verticalAlign="middle">
              <HStack spacing={0.5} flexWrap="nowrap">
                {/* Display name */}
                <Tooltip placement="left" label={explanation}>
                  <Text as="label" fontSize="13px" whiteSpace="nowrap">
                    {displayName}
                  </Text>
                </Tooltip>

                {/* Learn more link icon */}
                {learnMoreLink && (
                  <Tooltip placement="bottom" label="Learn more">
                    <IconButton
                      size="xs"
                      aria-label="Learn more"
                      onClick={() =>
                        window.open(
                          learnMoreLink,
                          '_blank',
                          'noopener,noreferrer',
                        )
                      }
                    >
                      <ShareExternalIcon boxSize={2.25} />
                    </IconButton>
                  </Tooltip>
                )}

                {/* Deprecation icon */}
                {deprecate && (
                  <Tooltip placement="bottom" label={deprecate}>
                    <WrenchIcon boxSize={2.25} color="feature.secondary" />
                  </Tooltip>
                )}
              </HStack>
            </Box>
            {/* Middle cell: availability badge */}
            <Box as="td" textAlign="right" verticalAlign="middle" px={0.5}>
              {availability !== FeatureFlagAvailability.CheckEntitlement && (
                <Badge
                  variant="subtle"
                  size="xs"
                  {...getAvailabilityBadgeProps(availability)}
                />
              )}
            </Box>
            {/* Growth tier cell */}
            <Box as="td" textAlign="center" verticalAlign="middle" px={0.5}>
              <TierCell
                tierSetting={tierInfo.growth}
                tierName="Growth"
                type={type}
                contracted={contracted}
              />
            </Box>
            {/* Pro tier cell */}
            <Box as="td" textAlign="center" verticalAlign="middle" px={0.5}>
              <TierCell
                tierSetting={tierInfo.pro}
                tierName="Pro"
                type={type}
                contracted={contracted}
              />
            </Box>
            {/* Enterprise tier cell */}
            <Box as="td" textAlign="center" verticalAlign="middle" px={0.5}>
              <TierCell
                tierSetting={tierInfo.ent}
                tierName="Enterprise"
                type={type}
                contracted={contracted}
              />
            </Box>
            {/* Enterprise Multi-site tier cell */}
            <Box as="td" textAlign="center" verticalAlign="middle" px={0.5}>
              <TierCell
                tierSetting={tierInfo.entMs}
                tierName="Enterprise Multi-site"
                type={type}
                contracted={contracted}
              />
            </Box>
            {/* Rightmost cell: input component */}

            <FormControl
              isInvalid={isInvalid}
              isRequired={isRequired}
              as="td"
              textAlign="center"
              verticalAlign="middle"
              width="fit-content"
              pr={1.5}
              pl={0.5}
            >
              {type == FeatureFlagType.Number ? (
                <NumberInput
                  field={field as FieldInputProps<number>}
                  form={form}
                  meta={meta as FieldMetaProps<number>}
                  flagData={flagData}
                />
              ) : type == FeatureFlagType.String ? (
                <StringSelectInput
                  field={field as FieldInputProps<string>}
                  form={form}
                  meta={meta as FieldMetaProps<string>}
                  flagData={flagData}
                />
              ) : type == FeatureFlagType.Boolean ? (
                <CheckboxInput
                  field={field as FieldInputProps<boolean>}
                  form={form}
                  meta={meta as FieldMetaProps<boolean>}
                  flagData={flagData}
                />
              ) : null}

              {fieldName && (
                <FormErrorMessage>
                  <ErrorMessage name={fieldName} />
                </FormErrorMessage>
              )}
            </FormControl>
          </Box>
        );
      }}
    </Field>
  );
};
